import { cn } from '@/lib/cn';
import { ChevronDown } from 'lucide-react';
import {
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  forwardRef,
  useId,
} from 'react';

/**
 * Pola formularzy (redesign 2026-09-30). 16 px na telefonie — iPhone nie
 * przybliza wtedy strony przy wejsciu w pole (audyt FORM-001); 14 px od `md`.
 * Wysokosc 44 px na telefonie / 38 px od `md`.
 */
const CONTROL =
  'block w-full rounded-md border border-border-strong bg-bg text-base text-fg shadow-sm transition-colors placeholder:text-fg-subtle hover:border-fg-subtle/60 focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent-ring disabled:cursor-not-allowed disabled:bg-surface-2 disabled:text-fg-muted md:text-sm';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return (
      <input
        ref={ref}
        className={cn(CONTROL, 'min-h-touch px-3 md:min-h-[38px]', className)}
        {...rest}
      />
    );
  },
);

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, rows = 4, ...rest }, ref) {
  return (
    <textarea ref={ref} rows={rows} className={cn(CONTROL, 'px-3 py-2.5', className)} {...rest} />
  );
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, ...rest }, ref) {
    return (
      <div className={cn('relative', className)}>
        <select
          ref={ref}
          className={cn(
            CONTROL,
            // pr-9: tekst nie wchodzi pod strzalke (audyt OF-003)
            'min-h-touch appearance-none bg-none pl-3 pr-9 md:min-h-[38px]',
          )}
          {...rest}
        >
          {children}
        </select>
        <ChevronDown
          className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-muted"
          strokeWidth={2}
        />
      </div>
    );
  },
);

/** Etykieta + pole + podpowiedz/blad. `children` dostaje wygenerowane `id`. */
export function Field({
  label,
  hint,
  error,
  required,
  children,
  className,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  children: (id: string) => ReactNode;
  className?: string;
}) {
  const id = useId();
  return (
    <div className={className}>
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-fg">
        {label}
        {required && <span className="ml-0.5 text-error">*</span>}
      </label>
      {children(id)}
      {error ? (
        <p className="mt-1.5 text-xs font-medium text-error" role="alert">
          {error}
        </p>
      ) : (
        hint && <p className="mt-1.5 text-xs text-fg-muted">{hint}</p>
      )}
    </div>
  );
}
